import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PasswordInput from '../ui/PasswordInput';
import PasswordStrengthMeter from '../ui/PasswordStrengthMeter';
import { validatePassword } from '../../lib/passwordValidator';
import logo from '../../assets/SenritsuXwordmarkwhite.png';
import '../../styles/Auth.css';

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const { updatePassword, user, profile } = useAuth();
  const navigate = useNavigate();

  const userContext = {
    email: user?.email || '',
    fullName: profile?.name || user?.user_metadata?.full_name || '',
    stageName: profile?.stage_name || user?.user_metadata?.stage_name || '',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Please enter a new password.');
      return;
    }

    const validation = validatePassword(password, userContext);
    if (!validation.isValid) {
      setError(validation.error || 'Password does not meet security requirements.');
      return;
    }

    if (!confirmPassword) {
      setError('Please confirm your new password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormInvalid = !password || !confirmPassword || !validatePassword(password, userContext).isValid || password !== confirmPassword;

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 460, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img src={logo} alt="SenritsuX" className="auth-brand-logo" style={{ marginBottom: '1.5rem' }} />
        </div>

        <h2 className="auth-heading">Reset Password</h2>
        <p className="auth-subheading">Enter your new password below.</p>

        {error && (
          <div className="auth-global-error">
            <span>⚠</span>
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div className="auth-global-success">
            <span>✓</span>
            <span>Password updated successfully! Redirecting to dashboard…</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="reset-password">New Password</label>
              <PasswordInput
                id="reset-password"
                placeholder="Min 8 characters"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError('');
                }}
                onEmojiAttempt={(msg) => setError(msg)}
                disabled={loading}
                autoComplete="new-password"
              />
              <PasswordStrengthMeter
                password={password}
                userContext={userContext}
                showChecklist={true}
              />
            </div>

            <div className="auth-form-group">
              <label className="auth-label" htmlFor="reset-confirm">Confirm New Password</label>
              <PasswordInput
                id="reset-confirm"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError('');
                }}
                onEmojiAttempt={(msg) => setError(msg)}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="auth-btn" disabled={loading || isFormInvalid}>
              {loading ? <span className="auth-spinner" /> : 'Update Password'}
            </button>
          </form>
        )}

        <div className="auth-footer-text">
          <Link to="/login">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}
